import { useCallback, useEffect, useMemo, useRef } from "react";
import type { SpectraPackage, SpectralState } from "../data/types";
import { formatOffsetKms, stateColor } from "../data/types";
import { LINE_GLYPHS, LINE_KEYS, type LineKey, type LineSolo } from "../domain/lines";
import { C_KMS } from "../domain/physics";
import type { CanvasPalette } from "../theme/canvasPalette";

const PADDING = { top: 16, right: 16, bottom: 36, left: 56 };
const AXIS_FONT = "11px Inter, system-ui, sans-serif";
const LABEL_FONT = "10px Inter, system-ui, sans-serif";

/** One spectrum drawn in the single-epoch view. */
export interface Trace {
  flux: Float32Array;
  state: SpectralState | null;
  /** Measured broad Hβ peak offset (km/s), drawn as a marker. Null = not measured. */
  hbetaOffsetKms: number | null;
}

/** One trace in the stacked Figure-1 view; the plot computes the vertical offsets itself. */
export interface StackedTrace {
  flux: Float32Array;
  color: string;
  label: string;
  alpha: number;
  lineWidth: number;
}

/** A detected spectral feature to mark on the plot (feature-sonification mode). */
export interface FeatureMarker {
  lambdaA: number;
  known: boolean;
}

export type PlotScene =
  | {
      kind: "single";
      primary: Trace;
      comparison: Trace | null;
      reddenedFlux: Float32Array | null;
      /** Draw primary minus comparison instead of the overlay. Needs a comparison. */
      showDifference: boolean;
      /** Detected features and estimated continuum from the feature sonifier. */
      featureMarkers: FeatureMarker[] | null;
      continuumFlux: Float32Array | null;
      /** Wavelength of the earcon-sweep pluck sounding right now, if any. */
      sweepLambdaA: number | null;
    }
  | { kind: "stacked"; traces: StackedTrace[] };

interface Props {
  pkg: SpectraPackage;
  scene: PlotScene;
  lineSolo: LineSolo;
  palette: CanvasPalette;
}

/** Pixel-space frame of the plot area plus the data-to-pixel maps. */
interface Frame {
  ctx: CanvasRenderingContext2D;
  left: number;
  right: number;
  top: number;
  bottom: number;
  xMin: number;
  xMax: number;
  xToPx: (lambda: number) => number;
  yToPx: (flux: number) => number;
}

interface TraceStyle {
  stroke: string;
  lineWidth: number;
  alpha?: number;
  dash?: number[];
}

/**
 * Half-width (Å) of each line's solo zoom window, applied around the feature's
 * rest wavelength from the manifest. Keeping the half-widths here but the
 * centres in the manifest means the zoom tracks whatever object is loaded.
 */
const SOLO_HALF_WIDTH_A: Record<LineKey, number> = {
  Halpha: 160,
  Hbeta: 100,
  Hgamma: 90,
  OIII: 70,
};

/** Build the ordered, labelled line list from the manifest's rest-frame features. */
function lineLabelsFromFeatures(
  features: Record<string, number>,
): Array<{ key: string; rest_A: number; label: string }> {
  return Object.entries(features)
    .map(([key, rest_A]) => ({ key, rest_A, label: (LINE_GLYPHS as Record<string, string>)[key] ?? key }))
    .sort((a, b) => a.rest_A - b.rest_A);
}

/** The x range for a solo: the line's zoom window, or the full grid. */
function xRangeFor(solo: LineSolo, features: Record<string, number>, wavelength: Float32Array): [number, number] {
  if ((LINE_KEYS as readonly string[]).includes(solo)) {
    const center = features[solo];
    if (typeof center === "number") {
      const half = SOLO_HALF_WIDTH_A[solo as LineKey];
      return [center - half, center + half];
    }
  }
  return [wavelength[0], wavelength[wavelength.length - 1]];
}

function finiteRange(arrays: ArrayLike<number>[]): [number, number] | null {
  let min = Infinity;
  let max = -Infinity;
  for (const arr of arrays) {
    for (let i = 0; i < arr.length; i += 1) {
      const v = arr[i];
      if (!Number.isFinite(v)) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  return Number.isFinite(min) && Number.isFinite(max) ? [min, max] : null;
}

/** Element-wise a - b, NaN wherever either side has no coverage. Double precision so pixels match the raw subtraction. */
function difference(a: Float32Array, b: Float32Array): Float64Array {
  const out = new Float64Array(a.length);
  for (let i = 0; i < a.length; i += 1) out[i] = a[i] - b[i];
  return out;
}

function shifted(flux: Float32Array, offset: number): Float64Array {
  const out = new Float64Array(flux.length);
  for (let i = 0; i < flux.length; i += 1) out[i] = flux[i] + offset;
  return out;
}

function yRangeForDifference(diff: Float64Array): [number, number] {
  const range = finiteRange([diff]);
  if (!range) return [-1, 1];
  const span = Math.max(Math.abs(range[0]), Math.abs(range[1])) * 1.05 || 1;
  return [-span, span];
}

function yRangeForOverlay(sources: Float32Array[]): [number, number] {
  const range = finiteRange(sources);
  if (!range) return [0, 1];
  const [min, max] = range;
  const pad = (max - min) * 0.08 || 1;
  return [Math.max(0, min - pad), max + pad];
}

/** Vertical offsets and y range for the stacked view. */
function stackedLayout(traces: StackedTrace[]): { offsets: number[]; yRange: [number, number] } {
  const maxFlux = Math.max(0, finiteRange(traces.map((t) => t.flux))?.[1] ?? 0) || 1;
  const stride = maxFlux * 0.45;
  const offsets = traces.map((_, i) => i * stride);
  const range = finiteRange(traces.map((t, i) => shifted(t.flux, offsets[i])));
  const [yMin, yMax] = range ?? [0, 1];
  const pad = (yMax - yMin) * 0.04 || 1;
  return { offsets, yRange: [yMin - pad, yMax + pad] };
}

export function SpectrumPlot(props: Props) {
  const { pkg, scene, lineSolo, palette } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wavelength = pkg.wavelength;
  const features = pkg.manifest.features;
  const lineLabels = useMemo(() => lineLabelsFromFeatures(features), [features]);
  const xRange = useMemo(() => xRangeFor(lineSolo, features, wavelength), [lineSolo, features, wavelength]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;
    const width = Math.max(1, Math.floor(cssWidth * dpr));
    const height = Math.max(1, Math.floor(cssHeight * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    const left = PADDING.left;
    const right = cssWidth - PADDING.right;
    const top = PADDING.top;
    const bottom = cssHeight - PADDING.bottom;
    if (right <= left || bottom <= top) return;

    const soloFill = palette.soloFill[lineSolo];
    if (soloFill !== "transparent") {
      ctx.fillStyle = soloFill;
      ctx.fillRect(left, top, right - left, bottom - top);
    }

    const stacked = scene.kind === "stacked" ? stackedLayout(scene.traces) : null;
    const diff =
      scene.kind === "single" && scene.showDifference && scene.comparison
        ? difference(scene.primary.flux, scene.comparison.flux)
        : null;
    const [yMin, yMax] = stacked
      ? stacked.yRange
      : diff
        ? yRangeForDifference(diff)
        : yRangeForOverlay(
            scene.kind === "single"
              ? [scene.primary.flux, scene.comparison?.flux, scene.reddenedFlux].filter(
                  (f): f is Float32Array => f != null,
                )
              : [],
          );
    const [xMin, xMax] = xRange;
    const frame: Frame = {
      ctx,
      left,
      right,
      top,
      bottom,
      xMin,
      xMax,
      xToPx: (lambda) => left + ((lambda - xMin) / (xMax - xMin)) * (right - left),
      yToPx: (flux) => bottom - ((flux - yMin) / (yMax - yMin)) * (bottom - top),
    };

    const yLabel = stacked
      ? "Fλ stacked (10⁻¹⁷ erg s⁻¹ cm⁻² Å⁻¹) + offset"
      : diff
        ? "Δ Fλ (10⁻¹⁷ erg s⁻¹ cm⁻² Å⁻¹)"
        : "Fλ (10⁻¹⁷ erg s⁻¹ cm⁻² Å⁻¹)";
    drawAxes(frame, palette, yMin, yMax, yLabel, diff !== null);
    drawLineGuides(frame, palette, lineLabels);

    if (scene.kind === "stacked" && stacked) {
      drawStacked(frame, wavelength, scene.traces, stacked.offsets);
    } else if (scene.kind === "single") {
      drawHbetaMarkers(frame, scene.primary, scene.comparison, features.Hbeta);
      if (diff) {
        strokeTrace(frame, wavelength, diff, { stroke: stateColor(scene.primary.state), lineWidth: 2 });
      } else {
        drawOverlay(frame, palette, wavelength, scene);
      }
      drawCoverageGaps(frame, wavelength, scene.primary.flux, palette.gap);
    }

    ctx.strokeStyle = palette.frame;
    ctx.strokeRect(left, top, right - left, bottom - top);
  }, [wavelength, features, scene, lineSolo, palette, xRange, lineLabels]);

  useEffect(() => {
    draw();
    const observer = new ResizeObserver(() => draw());
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [draw]);

  return (
    <div className="plot-canvas-wrap">
      <canvas ref={canvasRef} aria-label="Spectrum plot" />
    </div>
  );
}

function drawAxes(
  f: Frame,
  palette: CanvasPalette,
  yMin: number,
  yMax: number,
  yLabel: string,
  zeroLine: boolean,
): void {
  const { ctx } = f;
  ctx.strokeStyle = palette.grid;
  ctx.lineWidth = 1;
  ctx.font = AXIS_FONT;
  ctx.fillStyle = palette.axisText;
  for (const t of computeTicks(f.xMin, f.xMax, 6)) {
    const px = f.xToPx(t);
    ctx.beginPath();
    ctx.moveTo(px, f.top);
    ctx.lineTo(px, f.bottom);
    ctx.stroke();
    ctx.fillText(t.toFixed(0), px - 12, f.bottom + 14);
  }
  for (const t of computeTicks(yMin, yMax, 5)) {
    const py = f.yToPx(t);
    ctx.beginPath();
    ctx.moveTo(f.left, py);
    ctx.lineTo(f.right, py);
    ctx.stroke();
    ctx.fillText(t.toFixed(1), 4, py + 4);
  }
  if (zeroLine) {
    const py0 = f.yToPx(0);
    ctx.strokeStyle = palette.zeroLine;
    ctx.beginPath();
    ctx.moveTo(f.left, py0);
    ctx.lineTo(f.right, py0);
    ctx.stroke();
  }
  ctx.fillStyle = palette.axisText;
  ctx.fillText("Rest wavelength λ (Å)", (f.left + f.right) / 2 - 80, f.bottom + 28);
  ctx.save();
  ctx.translate(14, (f.top + f.bottom) / 2 + 80);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(yLabel, 0, 0);
  ctx.restore();
}

function drawLineGuides(
  f: Frame,
  palette: CanvasPalette,
  lines: Array<{ rest_A: number; label: string }>,
): void {
  const { ctx } = f;
  for (const line of lines) {
    if (line.rest_A < f.xMin || line.rest_A > f.xMax) continue;
    const px = f.xToPx(line.rest_A);
    ctx.strokeStyle = palette.emGuide;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(px, f.top);
    ctx.lineTo(px, f.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = palette.axisText;
    ctx.fillText(line.label, px + 3, f.top + 12);
  }
}

/** Mark each trace's measured Hβ peak with a vertical line and an offset tick from rest. */
function drawHbetaMarkers(f: Frame, primary: Trace, comparison: Trace | null, hbetaRestA: number): void {
  const { ctx } = f;
  const markers = [primary, comparison]
    .filter((t): t is Trace => t !== null && t.hbetaOffsetKms !== null)
    .map((t) => ({ kms: t.hbetaOffsetKms as number, color: stateColor(t.state) }));
  const restPx = f.xToPx(hbetaRestA);
  markers.forEach((marker, i) => {
    const peakA = hbetaRestA * (1 + marker.kms / C_KMS);
    if (peakA < f.xMin || peakA > f.xMax) return;
    const px = f.xToPx(peakA);
    const tickY = f.top + 22 + i * 12;
    ctx.strokeStyle = marker.color;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(px, f.top);
    ctx.lineTo(px, f.bottom);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(restPx, tickY);
    ctx.lineTo(px, tickY);
    ctx.stroke();
    ctx.fillStyle = marker.color;
    ctx.font = LABEL_FONT;
    const text = `Hβ peak ${formatOffsetKms(marker.kms)}`;
    const textX = marker.kms >= 0 ? px + 4 : px - 4 - ctx.measureText(text).width;
    ctx.fillText(text, textX, tickY - 3);
  });
}

function drawStacked(f: Frame, wavelength: Float32Array, traces: StackedTrace[], offsets: number[]): void {
  const { ctx } = f;
  // Lowest alpha first so the soloed (full-opacity) trace lands on top.
  const order = traces.map((_, i) => i).sort((a, b) => traces[a].alpha - traces[b].alpha);
  for (const k of order) {
    const trace = traces[k];
    strokeTrace(f, wavelength, shifted(trace.flux, offsets[k]), {
      stroke: trace.color,
      lineWidth: trace.lineWidth,
      alpha: trace.alpha,
    });
    const lastFlux = trace.flux.length > 0 ? trace.flux[trace.flux.length - 1] : 0;
    ctx.save();
    ctx.globalAlpha = Math.min(1, trace.alpha + 0.15);
    ctx.fillStyle = trace.color;
    ctx.font = LABEL_FONT;
    ctx.fillText(trace.label, f.right - 100, f.yToPx(offsets[k] + lastFlux));
    ctx.restore();
  }
}

function drawOverlay(
  f: Frame,
  palette: CanvasPalette,
  wavelength: Float32Array,
  scene: Extract<PlotScene, { kind: "single" }>,
): void {
  if (scene.comparison) {
    strokeTrace(f, wavelength, scene.comparison.flux, {
      stroke: stateColor(scene.comparison.state),
      lineWidth: 1.4,
      alpha: 0.6,
    });
  }
  if (scene.reddenedFlux) {
    strokeTrace(f, wavelength, scene.reddenedFlux, {
      stroke: palette.reddened,
      lineWidth: 1.5,
      alpha: 0.85,
      dash: [6, 4],
    });
  }
  strokeTrace(f, wavelength, scene.primary.flux, { stroke: stateColor(scene.primary.state), lineWidth: 2 });
  if (scene.continuumFlux) {
    strokeTrace(f, wavelength, scene.continuumFlux, {
      stroke: palette.axisText,
      lineWidth: 1,
      alpha: 0.5,
      dash: [2, 4],
    });
  }
  if (scene.featureMarkers) {
    drawFeatureMarkers(f, palette, wavelength, scene.primary.flux, scene.featureMarkers, scene.sweepLambdaA);
  }
}

function drawFeatureMarkers(
  f: Frame,
  palette: CanvasPalette,
  wavelength: Float32Array,
  primaryFlux: Float32Array,
  markers: FeatureMarker[],
  sweepLambdaA: number | null,
): void {
  const { ctx } = f;
  for (const marker of markers) {
    if (marker.lambdaA < f.xMin || marker.lambdaA > f.xMax) continue;
    const px = f.xToPx(marker.lambdaA);
    const color = marker.known ? palette.featureKnown : palette.featureDiscovery;
    const sounding = sweepLambdaA !== null && Math.abs(marker.lambdaA - sweepLambdaA) < 0.5;
    ctx.save();
    ctx.strokeStyle = color;
    ctx.globalAlpha = sounding ? 0.85 : 0.22;
    ctx.lineWidth = sounding ? 2 : 1;
    ctx.beginPath();
    ctx.moveTo(px, f.top + 26);
    ctx.lineTo(px, f.bottom);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.fillStyle = color;
    const half = sounding ? 7 : 5;
    const apex = sounding ? 29 : 25;
    ctx.beginPath();
    ctx.moveTo(px, f.top + apex);
    ctx.lineTo(px - half, f.top + 15);
    ctx.lineTo(px + half, f.top + 15);
    ctx.closePath();
    ctx.fill();
    if (sounding) {
      // Ring the spectrum itself where the sounding feature peaks.
      const flux = fluxAtWavelength(wavelength, primaryFlux, marker.lambdaA);
      if (flux !== null) {
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(px, f.yToPx(flux), 5, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
}

/** Stroke one series, lifting the pen across non-finite samples (coverage gaps). */
function strokeTrace(f: Frame, wavelength: Float32Array, values: ArrayLike<number>, style: TraceStyle): void {
  const { ctx } = f;
  ctx.save();
  ctx.globalAlpha = style.alpha ?? 1;
  ctx.strokeStyle = style.stroke;
  ctx.lineWidth = style.lineWidth;
  if (style.dash) ctx.setLineDash(style.dash);
  ctx.beginPath();
  let penDown = false;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!Number.isFinite(v)) {
      penDown = false;
      continue;
    }
    const x = f.xToPx(wavelength[i]);
    const y = f.yToPx(v);
    if (penDown) ctx.lineTo(x, y);
    else ctx.moveTo(x, y);
    penDown = true;
  }
  ctx.stroke();
  ctx.restore();
}

/** Flux at the sample nearest ``lambdaA`` (wavelength ascending), or null. */
function fluxAtWavelength(wavelength: Float32Array, flux: Float32Array, lambdaA: number): number | null {
  let lo = 0;
  let hi = wavelength.length - 1;
  if (hi < 0) return null;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (wavelength[mid] < lambdaA) lo = mid;
    else hi = mid;
  }
  const i = Math.abs(wavelength[lo] - lambdaA) <= Math.abs(wavelength[hi] - lambdaA) ? lo : hi;
  const v = flux[i];
  return Number.isFinite(v) ? v : null;
}

function drawCoverageGaps(f: Frame, wavelength: Float32Array, flux: Float32Array, gapColor: string): void {
  const { ctx } = f;
  let gapStart: number | null = null;
  ctx.save();
  ctx.fillStyle = gapColor;
  for (let i = 0; i <= flux.length; i += 1) {
    const lambda = i < flux.length ? wavelength[i] : Infinity;
    const hasData = i < flux.length && Number.isFinite(flux[i]);
    if (!hasData && gapStart === null && lambda >= f.xMin && lambda <= f.xMax) {
      gapStart = lambda;
    } else if ((hasData || lambda > f.xMax) && gapStart !== null) {
      const startLambda = Math.max(gapStart, f.xMin);
      const endLambda = Math.min(lambda, f.xMax);
      if (endLambda > startLambda) {
        const x0 = f.xToPx(startLambda);
        const x1 = f.xToPx(endLambda);
        ctx.fillRect(x0, f.bottom - 4, Math.max(1, x1 - x0), 4);
      }
      gapStart = null;
    }
  }
  ctx.restore();
}

export function computeTicks(min: number, max: number, target: number): number[] {
  const span = max - min;
  if (span <= 0) return [];
  const rough = span / target;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / magnitude;
  let step: number;
  if (norm < 1.5) step = magnitude;
  else if (norm < 3) step = 2 * magnitude;
  else if (norm < 7) step = 5 * magnitude;
  else step = 10 * magnitude;
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.0001; v += step) {
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks;
}
